import React, { useState, useEffect, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import { useAuth } from '../contexts/AuthContext'

function LureThread() {
  const { id, threadId } = useParams()
  const { user } = useAuth()
  const replyRef = useRef(null)

  const [baseLure, setBaseLure] = useState(null)
  const [thread, setThread] = useState(null)
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)

  // Reply form
  const [replyBody, setReplyBody] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [replyError, setReplyError] = useState('')

  // Edit state
  const [editingPostId, setEditingPostId] = useState(null)
  const [editBody, setEditBody] = useState('')
  const [editError, setEditError] = useState('')

  useEffect(() => {
    fetchData()
  }, [threadId])

  const fetchData = async () => {
    try {
      const [{ data: lureData }, { data: threadData }, { data: postsData }] = await Promise.all([
        supabase
          .from('base_lures')
          .select('base_lure_id, name, brands(brand_name)')
          .eq('base_lure_id', id)
          .single(),
        supabase
          .from('forum_threads')
          .select('*')
          .eq('thread_id', threadId)
          .single(),
        supabase
          .from('forum_posts')
          .select('*')
          .eq('thread_id', threadId)
          .eq('is_deleted', false)
          .order('created_at', { ascending: true }),
      ])

      setBaseLure(lureData)
      setThread(threadData)

      // Fetch usernames for all post authors
      const userIds = [...new Set((postsData || []).map(p => p.user_id))]
      const { data: usersData } = userIds.length > 0
        ? await supabase.from('users').select('id, username').in('id', userIds)
        : { data: [] }

      const userMap = {}
      usersData?.forEach(u => { userMap[u.id] = u.username })

      // Also get thread author username
      if (threadData) {
        const { data: threadUser } = await supabase
          .from('users')
          .select('username')
          .eq('id', threadData.user_id)
          .single()
        setThread(prev => ({ ...prev, username: threadUser?.username || 'Unknown' }))
        userMap[threadData.user_id] = threadUser?.username || 'Unknown'
      }

      setPosts((postsData || []).map(p => ({ ...p, username: userMap[p.user_id] || 'Unknown' })))
      setLoading(false)
    } catch (err) {
      console.error('Error fetching thread:', err)
      setLoading(false)
    }
  }

  const handleReply = async () => {
    if (!replyBody.trim()) { setReplyError('Reply cannot be empty.'); return }
    setSubmitting(true)
    setReplyError('')

    try {
      const { error } = await supabase
        .from('forum_posts')
        .insert([{ thread_id: parseInt(threadId), user_id: user.id, body: replyBody.trim() }])

      if (error) throw error

      setReplyBody('')
      await fetchData()
    } catch (err) {
      setReplyError(err.message || 'Failed to post reply.')
    } finally {
      setSubmitting(false)
    }
  }

  const startEdit = (post) => {
    setEditingPostId(post.post_id)
    setEditBody(post.body)
    setEditError('')
  }

  const cancelEdit = () => {
    setEditingPostId(null)
    setEditBody('')
    setEditError('')
  }

  const handleSaveEdit = async (postId) => {
    if (!editBody.trim()) { setEditError('Post cannot be empty.'); return }
    try {
      const { error } = await supabase
        .from('forum_posts')
        .update({ body: editBody.trim(), updated_at: new Date().toISOString() })
        .eq('post_id', postId)
        .eq('user_id', user.id)

      if (error) throw error
      setEditingPostId(null)
      await fetchData()
    } catch (err) {
      setEditError(err.message || 'Failed to save edit.')
    }
  }

  const handleDeletePost = async (postId, isFirstPost) => {
    const msg = isFirstPost
      ? 'Delete this post? The thread will remain but your opening message will be removed.'
      : 'Delete this reply?'
    if (!window.confirm(msg)) return

    try {
      const { error } = await supabase
        .from('forum_posts')
        .update({ is_deleted: true })
        .eq('post_id', postId)
        .eq('user_id', user.id)

      if (error) throw error
      await fetchData()
    } catch (err) {
      alert('Failed to delete post.')
    }
  }

  const handleDeleteThread = async () => {
    if (!window.confirm('Delete this entire discussion and all its posts?')) return
    try {
      const { error } = await supabase
        .from('forum_threads')
        .delete()
        .eq('thread_id', threadId)
        .eq('user_id', user.id)

      if (error) throw error
      window.location.href = `/lure/${id}/discussions`
    } catch (err) {
      alert('Failed to delete discussion.')
    }
  }

  const scrollToReply = () => {
    replyRef.current?.scrollIntoView({ behavior: 'smooth' })
    setTimeout(() => replyRef.current?.querySelector('textarea')?.focus(), 300)
  }

  if (loading) return <div className="loading">Loading discussion...</div>

  if (!thread) {
    return (
      <div className="error-page">
        <h2>Discussion Not Found</h2>
        <Link to={`/lure/${id}/discussions`} className="btn-primary">← Back to Discussions</Link>
      </div>
    )
  }

  const isThreadOwner = user && user.id === thread.user_id

  return (
    <div className="forum-page">
      <div className="breadcrumb">
        <Link to="/browse">Browse</Link>
        {' → '}
        <Link to={`/lure/${id}`}>{baseLure?.name}</Link>
        {' → '}
        <Link to={`/lure/${id}/discussions`}>Discussions</Link>
        {' → '}<span>{thread.title}</span>
      </div>

      {/* Thread header */}
      <div className="forum-thread-header">
        <div className="forum-thread-title-row">
          <h1>{thread.title}</h1>
          {thread.is_locked && <span className="ftl-locked-badge">🔒 Locked</span>}
        </div>
        <div className="forum-thread-meta">
          <span>Started by <Link to={`/profile/${thread.user_id}`}>{thread.username}</Link></span>
          <span>{new Date(thread.created_at).toLocaleDateString()}</span>
          <span>{posts.length} {posts.length === 1 ? 'post' : 'posts'}</span>
        </div>
        <div className="forum-thread-actions">
          {user && !thread.is_locked && (
            <button className="btn-secondary btn-sm" onClick={scrollToReply}>
              ↩ Reply
            </button>
          )}
          {isThreadOwner && (
            <button className="btn-reject btn-sm" onClick={handleDeleteThread}>
              Delete Discussion
            </button>
          )}
        </div>
      </div>

      {/* Posts */}
      <div className="forum-posts">
        {posts.map((post, index) => {
          const isOwn = user && user.id === post.user_id
          const isEditing = editingPostId === post.post_id

          return (
            <div key={post.post_id} className={`forum-post${index === 0 ? ' forum-post-first' : ''}`}>
              <div className="forum-post-author">
                <Link to={`/profile/${post.user_id}`} className="post-author-name">
                  {post.username}
                </Link>
                <span className="post-author-date">
                  {new Date(post.created_at).toLocaleDateString()}
                  {post.updated_at !== post.created_at && ' (edited)'}
                </span>
                {index === 0 && <span className="post-op-badge">OP</span>}
              </div>

              <div className="forum-post-body">
                {isEditing ? (
                  <div className="post-edit-form">
                    {editError && <div className="alert-error" style={{ marginBottom: '0.5rem' }}>{editError}</div>}
                    <textarea
                      value={editBody}
                      onChange={e => setEditBody(e.target.value)}
                      className="form-textarea"
                      rows="4"
                      autoFocus
                    />
                    <div className="form-actions" style={{ marginTop: '0.5rem' }}>
                      <button className="btn-secondary btn-sm" onClick={cancelEdit}>Cancel</button>
                      <button className="btn-primary btn-sm" onClick={() => handleSaveEdit(post.post_id)}>Save</button>
                    </div>
                  </div>
                ) : (
                  <p className="post-body-text">{post.body}</p>
                )}
              </div>

              {isOwn && !isEditing && (
                <div className="forum-post-actions">
                  <button className="post-action-btn" onClick={() => startEdit(post)}>Edit</button>
                  <button className="post-action-btn post-action-delete" onClick={() => handleDeletePost(post.post_id, index === 0)}>Delete</button>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Reply form */}
      <div className="forum-reply-section" ref={replyRef}>
        {thread.is_locked ? (
          <p className="forum-locked-note">🔒 This discussion is locked and no longer accepting replies.</p>
        ) : user ? (
          <div className="forum-reply-form">
            <h3>Post a Reply</h3>
            {replyError && <div className="alert-error" style={{ marginBottom: '0.75rem' }}>{replyError}</div>}
            <textarea
              value={replyBody}
              onChange={e => setReplyBody(e.target.value)}
              className="form-textarea"
              rows="4"
              placeholder="Write your reply..."
            />
            <div className="form-actions" style={{ marginTop: '0.75rem' }}>
              <button
                className="btn-primary"
                onClick={handleReply}
                disabled={submitting || !replyBody.trim()}
              >
                {submitting ? 'Posting...' : 'Post Reply'}
              </button>
            </div>
          </div>
        ) : (
          <p className="forum-login-note">
            <Link to="/login">Log in</Link> to reply to this discussion.
          </p>
        )}
      </div>
    </div>
  )
}

export default LureThread
